import Emitter from './Emitter'

class Player {
  constructor (source) {
    this.playerType = 'HTML'
    this.playerVersion = 5
    this.source = source
    this.mirroredElement = null
    this.mappedEvents = []
    this.mappedMethods = []
  }

  /**
   * Initialization
   *
   * @returns {void}
   */
  init () {
    this.element = this.createElement(this.source)
  }

  /**
   * Create a fake <video> element that will wrap any event or property to the real player
   *
   * @param {object} source
   *   Original player
   * @returns {HTMLVideoElement}
   *   Fake <video> element
   * @throws {Error}
   */
  createElement (source) {
    return document.createElement('video')
  }

  /**
   * Return the fake <video> element
   *
   * @returns {HTMLVideoElement}
   */
  getElement () {
    this.prepareElement()

    return this.element
  }

  mapEvent (eventName, eventHandler = null) {
    if (this.mirroredElement !== null) {
      this.mappedEvents.push(eventName)
      let _this = this
      this.mirroredElement.addEventListener(eventName, e => {
        let e2 = Emitter.cloneEvent('Event', e)
        _this.element.dispatchEvent(e2)
      })
    }
  }

  mapMethod (methodName, methodHandler = null) {
    if (methodHandler !== null) {
      this.element[methodName] = methodHandler
    } else if (this.mirroredElement !== null && typeof this.mirroredElement[methodName] !== 'undefined') {
      this.mappedMethods.push(methodName)
      this.element[methodName] = this.mirroredElement[methodName].bind(this.mirroredElement)
    }
  }

  mapProperty (propertyName, propertyHandler = null) {
    if (propertyHandler !== null) {
      Object.defineProperty(this.element, propertyName, propertyHandler)
    } else {
      let _this = this
      Object.defineProperty(this.element, propertyName, {
        get: function () {
          if (_this.mirroredElement === null) {
            return false
          }
          return _this.mirroredElement[propertyName]
        },
        set: function (value) {
          if (_this.mirroredElement === null) {
            return false
          }
          _this.mirroredElement[propertyName] = value
          return true
        }
      })
    }
  }

  mergeObjects (src1, src2) {
    let dst = {}

    for (let key in src1) {
      dst[key] = src1[key]
    }

    for (let key in src2) {
      dst[key] = src2[key]
    }

    return dst
  }

  /**
   * Prepare the <video> element
   *
   * @returns {void}
   */
  prepareElement () {
    let _this = this
    let _source = this.source

    Object.defineProperties(this.element, {
      player: {
        get: function () {
          return _source
        }
      },
      playerType: {
        get: function () {
          return _this.playerType
        }
      },
      playerVersion: {
        get: function () {
          return _this.playerVersion
        }
      },
      container: {
        get: function () {
          return _this.container
        }
      }
    })

    // Event handling helpers
    this.element.on = function (event, handler) {
      if (typeof event === 'string') {
        event = event.split(' ')
      } else if (!(event instanceof Array)) {
        throw new TypeError('Invalid argument')
      }
      event.forEach(e => {
        this.addEventListener(e, handler)
      })
      return this
    }

    this.element.off = function (event, handler) {
      if (typeof event === 'string') {
        event = event.split(' ')
      } else if (!(event instanceof Array)) {
        throw new TypeError('Invalid argument')
      }
      event.forEach(e => {
        this.removeEventListener(e, handler)
      })
      return this
    }

    this.element.trigger = function (eventType, eventData = {}) {
      this.dispatchEvent(Emitter.createEvent('Event', eventType, eventData))
    }
  }

  setMirroredElement (element) {
    if (this.mirroredElement === element) {
      return
    }
    this.mirroredElement = element

    this.mappedEvents.forEach(eventName => {
      let _this = this
      this.mirroredElement.addEventListener(eventName, e => {
        let e2 = Emitter.cloneEvent('Event', e)
        _this.element.dispatchEvent(e2)
      })
    })

    this.mappedMethods.forEach(methodName => {
      this.element[methodName] = this.mirroredElement[methodName].bind(this.mirroredElement)
    })
  }

  /**
   * Check if the current player wrapper class handles the specified original player
   *
   * @param {object} source
   *   Original player object
   * @returns {boolean}
   *   Whether the player is handled by the wrapper class or not
   */
  static handles (source) {
    return false
  }


  /**
   * Inserts an overlay in the player (countdown, etc)
   *
   * @param {object} element
   *   DOM Element to insert
   * @returns {Promise}
   *   Promise to be executed when overlay has been inserted.
   */
  insertOverlay (element) {
    throw new Error('Not Implemented!')
  }

  /**
   * Changes the order of icons according to the order given
   *
   * @param {array} order
   */
  reorderUI (order) {
    throw new Error('Not Implemented')
  }

  inverseVolumebar () {
    throw new Error('Not Implemented')
  }

  /**
   * Inserts an overlay in the player (countdown, etc)
   *
   * @param {DOMElement} icon
   * @param {object} position { before: '', after: '' }
   */
  insertIcon (icon, position) {
    throw new Error('Not Implemented')
  }

}

export default Player
